/**
 * ThriftLinkService
 * API client for SIFT-THRIFT account linking operations.
 */

import axios, { AxiosInstance, AxiosError } from 'axios';
import { AuthService } from './AuthService';
import config from '../config';

export interface ThriftLinkStatus {
  linked: boolean;
  thrift_email?: string;
  thrift_display_name?: string;
  linked_at?: string;
}

export interface LinkInitiateResponse {
  link_token: string;
  redirect_url: string;
  expires_in_seconds: number;
}

export class ThriftLinkService {
  private axiosInstance: AxiosInstance;
  private authService: AuthService;

  constructor() {
    this.authService = AuthService.getInstance();

    this.axiosInstance = axios.create({
      baseURL: config.apiBaseUrl,
      timeout: 15000,
    });

    this.axiosInstance.interceptors.request.use(
      async (cfg) => {
        try {
          const token = await this.authService.getValidAccessToken();
          if (token) {
            cfg.headers.Authorization = `Bearer ${token}`;
          }
        } catch (error) {
          console.error('ThriftLinkService: failed to get access token:', error);
        }
        return cfg;
      },
      (error) => Promise.reject(error)
    );
  }

  /**
   * Initiate the linking flow. Returns a redirect URL to THRIFT.
   */
  async initiateLinking(): Promise<LinkInitiateResponse> {
    try {
      const response = await this.axiosInstance.post<LinkInitiateResponse>(
        '/api/thrift/link/initiate'
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Check the current link status.
   */
  async getLinkStatus(): Promise<ThriftLinkStatus> {
    try {
      const response = await this.axiosInstance.get<ThriftLinkStatus>(
        '/api/thrift/link/status'
      );
      return response.data;
    } catch (error) {
      // On server errors (503, 500, etc.), degrade gracefully to "not linked"
      // rather than showing a scary error message to the user.
      if (axios.isAxiosError(error) && error.response && error.response.status >= 500) {
        console.warn('ThriftLinkService: status check failed with', error.response.status, '— defaulting to unlinked');
        return { linked: false };
      }
      throw this.handleError(error);
    }
  }

  /**
   * Get info about a reverse link token (THRIFT-initiated flow).
   */
  async getReverseTokenInfo(token: string): Promise<{
    valid: boolean;
    already_linked: boolean;
    thrift_email: string;
    thrift_display_name: string;
  }> {
    try {
      const response = await this.axiosInstance.get(
        `/api/thrift/link/reverse-token-info?token=${encodeURIComponent(token)}`
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Complete the reverse link (THRIFT-initiated flow). SIFT user confirms.
   */
  async completeFromThrift(token: string): Promise<{
    status: string;
    sift_user_id: string;
    thrift_user_id: string;
    callback_url: string;
  }> {
    try {
      const response = await this.axiosInstance.post(
        '/api/thrift/link/complete-from-thrift',
        { token }
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Unlink the current SIFT account from THRIFT.
   */
  async unlinkThrift(): Promise<void> {
    try {
      await this.axiosInstance.delete('/api/thrift/link');
    } catch (error) {
      throw this.handleError(error);
    }
  }

  private handleError(error: unknown): Error {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<{ detail?: string | { error?: string; thrift_email?: string } }>;
      if (axiosError.response) {
        const status = axiosError.response.status;
        const detail = axiosError.response.data?.detail;
        if (status === 401) return new Error('Authentication failed. Please log in again.');
        if (status === 409) {
          const msg = typeof detail === 'object' && detail?.error === 'already_linked'
            ? `Already linked to THRIFT (${detail?.thrift_email || 'unknown'})`
            : 'Account already linked.';
          return new Error(msg);
        }
        if (status === 404) return new Error('No linked THRIFT account found.');
        if (status === 503) return new Error('THRIFT linking service is temporarily unavailable. Please try again shortly.');
        const message = typeof detail === 'string' ? detail : 'Operation failed.';
        return new Error(message);
      }
      if (axiosError.code === 'ECONNABORTED') return new Error('Request timed out.');
      return new Error('Network error. Please check your connection.');
    }
    if (error instanceof Error) return error;
    return new Error('An unexpected error occurred.');
  }
}

export default ThriftLinkService;
